<template>
  <v-app>
    <v-container fluid class="order-screen pa-0">
      <div class="order-header">
        <v-btn class="back-btn" rounded depressed @click="goBack()">
          <v-icon left color="#00378e" size="45">mdi-chevron-left</v-icon>
          Back
        </v-btn>
        <div class="order-page-title">Order</div>
      </div>

      <div v-if="loadingMenu" class="order-state">
        Loading menu...
      </div>

      <div v-else-if="menuLoadError" class="order-state" role="status">
        {{ menuLoadError }}
        <v-btn text :disabled="isFetchingMenu" @click="loadMenuItems(false, true)">Retry</v-btn>
      </div>

      <div v-else-if="menuItems.length === 0" class="order-state">
        No menu available
      </div>

      <div v-else class="order-content">
        <div
          v-for="item in menuItems"
          :key="item.id"
          class="order-card"
          :class="{ added: isAddedItem(item) }"
        >
          <img class="drink-photo" :src="item.image" :alt="item.name" />
          <div class="drink-name">{{ item.name }}</div>

          <div v-if="item.temperatureOptions.length" class="option-group">
            <div class="option-label">Temperature</div>
            <label
              v-for="option in item.temperatureOptions"
              :key="option.value"
              class="option-item"
            >
              <input
                v-model="item.temperature"
                type="radio"
                :value="option.value"
              />
              <span>{{ option.label }}</span>
            </label>
          </div>

          <div
            v-if="item.sweetnessOptions.length"
            class="option-group sweetness-group"
          >
            <div class="option-label">Sweetness</div>
            <label
              v-for="option in item.sweetnessOptions"
              :key="option.value"
              class="option-item"
            >
              <input
                v-model="item.sweetness"
                type="radio"
                :value="option.value"
              />
              <span>{{ option.label }}</span>
            </label>
          </div>

          <div class="quantity-row">
            <v-btn icon class="qty-btn minus" @click="decrease(item)">
              <v-icon>mdi-minus-circle-outline</v-icon>
            </v-btn>
            <span>{{ item.quantity }}</span>
            <v-btn icon class="qty-btn plus" @click="increase(item)">
              <v-icon>mdi-plus-circle-outline</v-icon>
            </v-btn>
          </div>

          <button
            class="add-cart-btn"
            :class="{ added: isAddedItem(item) }"
            type="button"
            @click="addToCart(item)"
          >
            {{ isAddedItem(item) ? "Added" : "Add to cart" }}
          </button>
        </div>
      </div>

      <button
        class="cart-float"
        :class="{ pulse: cartPulse }"
        type="button"
        @click="openCart()"
      >
        <v-img
          src="@/assets/icons/cart2.png"
          width="96"
          height="96"
          contain
        />
        <span>{{ cartCount }}</span>
      </button>
    </v-container>
  </v-app>
</template>

<script>
import enurl from "@/api/environment";
import Swal from "sweetalert2/dist/sweetalert2.js";
import { loadBeverageMenu, getMenuKey, BEVERAGE_MENU_TTL_MS } from "@/utils/beverageMenuCache";
import { addOrderCartItem, clearOrderCart, getOrderCartCount } from "@/utils/orderCart";

const localImages = {
  americano: require("@/assets/order/americano.png"),
  espresso: require("@/assets/order/espresso.png"),
  water: require("@/assets/order/water.png"),
};

let cachedMenuItems = null;
let cachedMenuSource = null;
let cachedMenuScope = "";
const INACTIVITY_TIMEOUT_MS = 60 * 1000;

export default {
  name: "Order",
  data() {
    return {
      url: enurl.apiUrl,
      loadingMenu: false,
      menuLoadError: "",
      isFetchingMenu: false,
      menuRefreshInterval: null,
      cartCount: 0,
      addedItemKey: "",
      addFeedbackTimer: null,
      inactivityTimer: null,
      cartPulse: false,
      menuItems: [],
    };
  },

  mounted() {
    ["touchstart", "mousedown", "keydown"].forEach((eventName) => {
      window.addEventListener(eventName, this.resetInactivityTimer, true);
    });
    this.resetInactivityTimer();
    this.refreshCartCount();
    this.loadMenuItems();
    this.menuRefreshInterval = setInterval(() => {
      if (!document.hidden) this.loadMenuItems(true, true);
    }, BEVERAGE_MENU_TTL_MS);
    document.addEventListener("visibilitychange", this.refreshVisibleMenu);
  },

  watch: {
    "$route.query.eventID"() {
      this.refreshCartCount();
    },
  },

  beforeDestroy() {
    ["touchstart", "mousedown", "keydown"].forEach((eventName) => {
      window.removeEventListener(eventName, this.resetInactivityTimer, true);
    });
    document.removeEventListener("visibilitychange", this.refreshVisibleMenu);
    clearInterval(this.menuRefreshInterval);
    clearTimeout(this.inactivityTimer);
    clearTimeout(this.addFeedbackTimer);
    if (this.menuItems.length && this._menuSource === cachedMenuSource && this._menuScope === cachedMenuScope) {
      cachedMenuItems = this.cloneMenuItems(this.menuItems);
    }
  },

  methods: {
    resetInactivityTimer() {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = setTimeout(() => {
        this.$router.replace("/MeetingRoomScreen");
      }, INACTIVITY_TIMEOUT_MS);
    },

    refreshCartCount() {
      this.cartCount = getOrderCartCount(this.$route.query.eventID || "");
    },

    goBack() {
      this.$router.push("/MeetingRoomScreen");
    },

    openCart() {
      this.$router.push({
        path: "/MyOrder",
        query: {
          eventID: this.$route.query.eventID || "",
          roomName: this.$route.query.roomName || "",
        },
      });
    },

    decrease(item) {
      if (item.quantity > 1) {
        item.quantity -= 1;
      }
    },

    increase(item) {
      item.quantity += 1;
    },

    refreshVisibleMenu() {
      if (!document.hidden) this.loadMenuItems(true);
    },

    async loadMenuItems(silent = false, force = false) {
      if (this.isFetchingMenu) return;
      this.isFetchingMenu = true;
      this.loadingMenu = this.menuItems.length === 0;
      const menuScope = getMenuKey(this.url);
      try {
        const res = await loadBeverageMenu(this.url, force);
        if (this._isDestroyed || this._isBeingDestroyed) return;

        if (res.status === 0) {
          const data = this.getResponseList(res.data);
          if (res !== this._menuSource || this.menuItems.length === 0) {
            const previousItems = menuScope === cachedMenuScope
              ? (this.menuItems.length ? this.menuItems : cachedMenuItems || [])
              : [];
            const previousByID = new Map(previousItems.map((item) => [item.id, item]));
            this.menuItems = data.map((item) => {
              const mapped = this.mapMenuItem(item);
              const previous = previousByID.get(mapped.id);
              if (previous) {
                mapped.quantity = previous.quantity;
                if (mapped.temperatureOptions.some((option) => option.value === previous.temperature)) mapped.temperature = previous.temperature;
                if (mapped.sweetnessOptions.some((option) => option.value === previous.sweetness)) mapped.sweetness = previous.sweetness;
              }
              return mapped;
            });
          }
          this._menuSource = res;
          this._menuScope = menuScope;
          cachedMenuSource = res;
          cachedMenuScope = menuScope;
          cachedMenuItems = this.cloneMenuItems(this.menuItems);
          this.menuLoadError = "";
          return;
        }

        throw new Error(res.message || "Load menu failed");
      } catch (error) {
        if (this._isDestroyed || this._isBeingDestroyed) return;
        if (this.menuItems.length && this._menuSource === cachedMenuSource && this._menuScope === cachedMenuScope) {
          cachedMenuItems = this.cloneMenuItems(this.menuItems);
        }
        this.menuItems = [];
        const message =
          error?.response?.data?.message ||
          error?.message ||
          "Cannot connect to server";
        this.menuLoadError = "โหลดเมนูไม่สำเร็จ กรุณาลองใหม่";
        if (silent) return;

        Swal.fire({
          icon: "warning",
          width: 900,
          text: message,
        });
      } finally {
        this.loadingMenu = false;
        this.isFetchingMenu = false;
      }
    },

    getResponseList(data) {
      if (Array.isArray(data)) return data;
      if (Array.isArray(data?.listData)) return data.listData;
      if (Array.isArray(data?.ListData)) return data.ListData;
      return [];
    },

    cloneMenuItems(items) {
      return (items || []).map((item) => ({
        ...item,
        temperatureOptions: [...(item.temperatureOptions || [])],
        sweetnessOptions: [...(item.sweetnessOptions || [])],
      }));
    },

    mapMenuItem(item) {
      const temperatureOptions = this.mapTemperatureOptions(item);
      const sweetnessOptions = this.mapSweetnessOptions(item);
      const imageKey = this.getField(item, ["imageKey", "beverageImage", "image"]);

      return {
        id: this.getField(item, ["id", "beverageID", "BeverageID"]),
        name: this.getField(item, ["name", "beverageName", "BeverageName"]) || "-",
        image: this.resolveImage(imageKey),
        imageKey: imageKey || "water",
        temperatureOptions,
        sweetnessOptions,
        temperature: temperatureOptions[0]?.value || "",
        sweetness: sweetnessOptions[0]?.value || "",
        quantity: 1,
      };
    },

    getCartItemKey(item) {
      return [item.id, item.temperature, item.sweetness || "none"].join("|");
    },

    isAddedItem(item) {
      return this.addedItemKey === this.getCartItemKey(item);
    },

    showAddFeedback(item) {
      clearTimeout(this.addFeedbackTimer);
      this.addedItemKey = this.getCartItemKey(item);
      this.cartPulse = false;
      this.$nextTick(() => {
        this.cartPulse = true;
      });

      this.addFeedbackTimer = setTimeout(() => {
        this.addedItemKey = "";
        this.cartPulse = false;
      }, 900);
    },

    mapTemperatureOptions(item) {
      const options =
        this.getField(item, ["temperatureOptions", "temperatures", "temperatureList"]) ||
        [];

      return options.map((option) => ({
        value: this.getField(option, ["value", "temperatureID", "TemperatureID"]),
        label:
          this.getField(option, [
            "label",
            "temperatureName",
            "TemperatureName",
            "temperatureCode",
            "TemperatureCode",
          ]) || "-",
      }));
    },

    mapSweetnessOptions(item) {
      const options =
        this.getField(item, ["sweetnessOptions", "sweetnesses", "sweetnessList"]) ||
        [];

      return options.map((option) => {
        const percent = this.getField(option, [
          "sweetnessPercent",
          "SweetnessPercent",
        ]);

        return {
          value: this.getField(option, ["value", "sweetnessID", "SweetnessID"]),
          label:
            this.getField(option, ["label", "sweetnessName", "SweetnessName"]) ||
            (percent || percent === 0 ? `${percent} %` : "-"),
        };
      });
    },

    getField(source, fields) {
      return fields.reduce((value, field) => {
        return value !== undefined && value !== null ? value : source?.[field];
      }, undefined);
    },

    resolveImage(image) {
      if (!image) return localImages.water;

      const source = String(image).trim();
      if (/^(https?:)?\/\//.test(source) || source.startsWith("data:")) {
        return source;
      }

      if (this.isBase64Image(source)) {
        return `data:image/png;base64,${source.replace(/\s/g, "")}`;
      }

      const fileName = source.split("/").pop();
      const imageKey = fileName.replace(/\.[^/.]+$/, "").toLowerCase();
      return localImages[imageKey] || source;
    },

    isBase64Image(value) {
      const source = value.replace(/\s/g, "");
      return source.length > 100 && /^[A-Za-z0-9+/]+={0,2}$/.test(source);
    },

    addToCart(item) {
      const eventID = this.$route.query.eventID || "";
      if (!eventID) {
        clearOrderCart();
        this.cartCount = 0;
        Swal.fire({
          icon: "warning",
          width: 900,
          text: "ไม่พบ EventID ของห้องประชุม กรุณากลับไปหน้า Meeting Room แล้วกด Order ใหม่",
        });
        return;
      }
      addOrderCartItem(item, eventID);
      this.cartCount = getOrderCartCount(eventID);
      this.showAddFeedback(item);
    },
  },
};
</script>

<style scoped>
.order-screen {
  min-height: 100vh;
  background: #eaf4ff url("~@/assets/backgroundroom.png");
  background-size: cover;
  background-position: left bottom;
  position: relative;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 41px 31px 70px !important;
}

.order-header {
  width: 100%;
  height: 86px;
  border-radius: 43px;
  background: #003f96;
  display: flex;
  align-items: center;
  position: relative;
  box-shadow: 0 6px 6px rgba(0, 0, 0, 0.25);
}

.back-btn {
  position: absolute;
  left: 18px;
  height: 55px !important;
  min-width: 124px !important;
  background: white !important;
  color: #00378e !important;
  font-size: 24px;
  text-transform: none;
  padding: 0 22px 0 12px !important;
}

.order-page-title {
  width: 100%;
  text-align: center;
  color: white;
  font-size: 40px;
  font-weight: 800;
}

.order-content {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 381px));
  gap: 27px;
  justify-content: center;
  margin: 80px auto 0;
}

.order-state {
  margin: 90px auto 0;
  color: #003f96;
  font-size: 28px;
  font-weight: 800;
  text-align: center;
}

.order-card {
  width: 100%;
  max-width: 381px;
  height: 598.8826px;
  background: rgba(255, 255, 255, 0.94);
  border-radius: 24px;
  padding: 18px 18px 20px;
  box-shadow: 0 4px 5px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  transition: box-shadow 0.18s ease, transform 0.18s ease;
}

.order-card.added {
  animation: added-card-pop 0.45s ease;
  box-shadow: 0 8px 18px rgba(0, 63, 150, 0.25);
}

.drink-photo {
  width: 100%;
  height: 216px;
  border-radius: 20px;
  display: block;
  object-fit: cover;
  object-position: center;
}

.drink-name {
  margin-top: 18px;
  color: #003f96;
  font-size: 30px;
  font-weight: 800;
  text-align: center;
  line-height: 1.1;
}

.option-group {
  margin-top: 14px;
  padding: 0 18px;
  display: flex;
  align-items: center;
  gap: 22px;
  flex-wrap: wrap;
}

.sweetness-group {
  margin-top: 34px;
  gap: 28px;
}

.option-label {
  width: 100%;
  color: #003f96;
  font-size: 16px;
  line-height: 1;
}

.option-item {
  color: #003f96;
  font-size: 16px;
  display: inline-flex;
  align-items: center;
  gap: 9px;
}

.option-item input {
  width: 17px;
  height: 17px;
  accent-color: #111;
}

.quantity-row {
  margin: auto auto 23px;
  width: 178px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: #003f96;
  font-size: 24px;
}

.qty-btn {
  width: 36px !important;
  height: 36px !important;
}

.qty-btn.minus {
  color: red !important;
}

.qty-btn.plus {
  color: #003f96 !important;
}

.add-cart-btn {
  width: 100%;
  flex: 0 0 auto;
  height: 48px;
  border: 0;
  border-radius: 24px;
  background: #003f96;
  color: white;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 3px 4px rgba(0, 0, 0, 0.22);
  transition: background 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease;
}

.add-cart-btn.added {
  background: #17a05d;
  transform: translateY(-1px);
  box-shadow: 0 6px 12px rgba(23, 160, 93, 0.25);
}

.cart-float {
  position: absolute;
  right: 70px;
  bottom: 52px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
}

.cart-float span {
  position: absolute;
  top: 0;
  right: -6px;
  width: 27px;
  height: 27px;
  border-radius: 50%;
  background: #003f96;
  color: white;
  font-size: 18px;
  font-weight: 800;
  line-height: 27px;
}

.cart-float.pulse span {
  animation: cart-badge-pop 0.45s ease;
}

@keyframes added-card-pop {
  0% {
    transform: scale(1);
  }
  45% {
    transform: scale(1.018);
  }
  100% {
    transform: scale(1);
  }
}

@keyframes cart-badge-pop {
  0% {
    transform: scale(1);
  }
  45% {
    transform: scale(1.35);
  }
  100% {
    transform: scale(1);
  }
}

@media only screen and (min-width: 761px) and (max-width: 1366px) {
  .order-screen {
    padding: 24px 24px 90px !important;
  }

  .order-header {
    width: 100%;
    height: 68px;
  }

  .back-btn {
    height: 46px !important;
    min-width: 104px !important;
    font-size: 18px;
  }

  .order-page-title {
    font-size: 32px;
  }

  .order-content {
    width: 100%;
    max-width: 1160px;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 20px;
    margin-top: 36px;
  }

  .order-card {
    height: auto;
    min-height: 500px;
    padding: 16px 16px 18px;
  }

  .drink-photo {
    height: 168px;
  }

  .drink-name {
    margin-top: 14px;
    font-size: 24px;
  }

  .option-group {
    margin-top: 12px;
    padding: 0 8px;
    gap: 12px;
  }

  .sweetness-group {
    margin-top: 18px;
    gap: 16px;
  }

  .quantity-row {
    width: 150px;
    margin-bottom: 16px;
  }

  .add-cart-btn {
    height: 44px;
  }

  .cart-float {
    right: 28px;
    bottom: 24px;
  }
}

@media only screen and (min-width: 761px) and (max-width: 959px) {
  .order-content {
    max-width: 760px;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media only screen and (max-width: 760px) {
  .order-screen {
    padding: 16px 16px calc(96px + env(safe-area-inset-bottom)) !important;
    overflow: auto;
  }

  .order-header {
    width: 100%;
    min-width: 0;
    height: 62px;
  }

  .back-btn {
    width: 44px;
    height: 44px !important;
    min-width: 44px !important;
    padding: 0 !important;
    font-size: 0;
  }

  .order-page-title {
    padding: 0 48px;
    font-size: 24px;
  }

  .order-content {
    grid-template-columns: 1fr;
    width: 100%;
    max-width: 420px;
    gap: 20px;
    margin: 24px auto 0;
  }

  .order-card {
    width: 100%;
    max-width: none;
    height: auto;
    min-height: 520px;
  }

  .drink-photo {
    height: 210px;
  }

  .cart-float {
    position: fixed;
    right: 16px;
    bottom: calc(12px + env(safe-area-inset-bottom));
  }

  .cart-float .v-image {
    width: 72px !important;
    height: 72px !important;
  }

  .back-btn ::v-deep .v-icon {
    margin: 0 !important;
  }
}
</style>
